import { useState } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { RefreshCw, X } from 'lucide-react'
import { useTranslation } from '@cravo/i18n'

const RELOAD_PROMPT_DISMISSED_KEY = 'cravo-pwa-reload-dismissed-at'

/**
 * Registers the service worker and shows a banner when a new app version
 * is ready. Clicking "Reload" applies the update and refreshes the page.
 */
export function PwaUpdater() {
  const { t } = useTranslation()
  const [dismissedAt, setDismissedAt] = useState<number>(() => {
    if (typeof localStorage === 'undefined') return 0
    return Number(localStorage.getItem(RELOAD_PROMPT_DISMISSED_KEY) ?? 0)
  })

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return
      // Periodically check for updates (every 30 minutes)
      setInterval(() => {
        if (document.visibilityState === 'visible') {
          void registration.update()
        }
      }, 30 * 60 * 1000)
    },
    onRegisterError() {
      // Service worker registration errors are non-critical for app function
    },
  })

  const handleReload = () => {
    void updateServiceWorker(true)
  }

  const handleDismissUpdate = () => {
    const now = Date.now()
    setDismissedAt(now)
    localStorage.setItem(RELOAD_PROMPT_DISMISSED_KEY, String(now))
    setNeedRefresh(false)
  }

  // Suppress update banner for 4 hours after last dismiss
  const suppressUpdate = needRefresh && Date.now() - dismissedAt < 4 * 60 * 60 * 1000

  if (!needRefresh || suppressUpdate) return null

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        top: 16,
        background: 'var(--ink-950)',
        color: 'white',
        padding: '12px 16px',
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        zIndex: 1100,
        boxShadow: '0 12px 32px rgba(15,23,42,0.25)',
        maxWidth: 'calc(100vw - 32px)',
      }}
    >
      <RefreshCw size={16} style={{ color: 'var(--brand-pink)' }} />
      <div style={{ fontSize: 13, fontWeight: 500 }}>{t('components.pwaUpdater.newVersion')}</div>
      <button
        type="button"
        onClick={handleReload}
        style={{
          background: 'var(--brand-pink)',
          color: 'white',
          border: 'none',
          padding: '6px 12px',
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        {t('components.pwaUpdater.reload')}
      </button>
      <button
        type="button"
        onClick={handleDismissUpdate}
        aria-label={t('components.pwaUpdater.dismiss')}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'rgba(255,255,255,0.7)',
          cursor: 'pointer',
          padding: 4,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <X size={14} />
      </button>
    </div>
  )
}
